import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EventRsvpControls } from "@/components/live/EventRsvpControls";
import { EventAttendeesList } from "@/components/live/EventAttendeesList";
import { computeEventStatus, resolveJoinAction, isRsvpOpen, useEventClock } from "./LivePage.eventState";
import { optimizeImage } from "@/lib/mediaUrls";

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, {
  weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
});

// Public, shareable event page — viewable without logging in.
export default function LiveEventPage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [attendeesKey, setAttendeesKey] = useState(0);
  const autoRsvpDone = useRef(false);
  const nowTick = useEventClock(15000);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/live/events/${eventId}`);
      setEv(r.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  }, [eventId]);

  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setCurrentUser(r.data)).catch(() => setCurrentUser(null));
  }, [load]);

  const status = ev ? computeEventStatus(ev, nowTick) : null;
  const rsvpOpen = ev ? isRsvpOpen(ev, status) : false;

  // Return-from-login: complete the RSVP the visitor started.
  useEffect(() => {
    if (autoRsvpDone.current || !ev || !currentUser || searchParams.get("rsvp") !== "1") return;
    autoRsvpDone.current = true;
    navigate(`/live/events/${eventId}`, { replace: true });
    if (!rsvpOpen || ev.viewer_rsvped || currentUser.user_id === ev.host_user_id) return;
    api.post(`/live/events/${eventId}/rsvp`)
      .then((r) => {
        setEv((p) => ({ ...p, viewer_rsvped: true, rsvp_count: r.data.rsvp_count }));
        toast.success("Your spot is reserved!");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not reserve your spot"));
  }, [ev, currentUser, searchParams, rsvpOpen, eventId, navigate]);

  const onUpdated = (_id, patch) => {
    setEv((p) => ({ ...p, ...patch }));
    setAttendeesKey((k) => k + 1);
  };

  if (notFound) {
    return (
      <Shell>
        <Card className="p-10 text-center bg-black/40 border-purple-500/20" data-testid="live-event-not-found">
          <p className="text-white text-lg font-bold mb-2">Event not found</p>
          <p className="text-gray-400 mb-6">This event may have been removed by the host.</p>
          <Link to="/live"><Button className="bg-purple-700 text-white">Browse Live Events</Button></Link>
        </Card>
      </Shell>
    );
  }
  if (!ev) {
    return <Shell><p className="text-gray-400" data-testid="live-event-loading">Loading event…</p></Shell>;
  }

  const join = resolveJoinAction(ev, status);
  const started = join.kind === "livekit_room" && !join.disabled && !!ev.started_room_at;
  const canManage = currentUser && (currentUser.user_id === ev.host_user_id
    || ["admin", "moderator"].includes(currentUser.role));
  const hostName = ev.host ? (ev.host.artist_name || ev.host.name) : null;

  return (
    <Shell>
      <Card className="overflow-hidden p-0 bg-gradient-to-br from-purple-900/30 to-black border-purple-500/30" data-testid="live-event-page">
        <div className="relative aspect-[16/9] bg-black">
          <img src={optimizeImage(ev.cover_image || "/rgm-og-image.png", 'cover')} alt="" className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = "/rgm-og-image.png"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          <StatusChip status={status} started={started} />
        </div>
        <div className="p-6 sm:p-8 flex flex-col gap-5">
          <div>
            <p className="text-xs text-purple-300 uppercase tracking-widest font-bold">
              {(ev.program_type || "").replace(/_/g, " ") || "RGM Studio Live"}
            </p>
            <h1 className="text-3xl sm:text-4xl font-black text-white mt-1" data-testid="live-event-title">{ev.title}</h1>
            <p className="text-gray-300 mt-3 flex items-center gap-2" data-testid="live-event-when">
              <CalendarDays className="w-4 h-4 text-purple-300" /> {fmtDate(ev.start_at)}
            </p>
            {hostName && (
              <Link to={`/artist/${ev.host.user_id}`} className="text-sm text-purple-300 hover:text-purple-200 inline-block mt-2" data-testid="live-event-host">
                Hosted by {hostName}
              </Link>
            )}
          </div>
          {ev.description && <p className="text-gray-300 whitespace-pre-line" data-testid="live-event-description">{ev.description}</p>}

          {started && (
            <Link to={join.href} className="block">
              <Button className="w-full font-bold bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white" data-testid="live-event-join">
                {join.label}
              </Button>
            </Link>
          )}
          {ev.is_rgm_studio ? (
            <EventRsvpControls ev={ev} currentUser={currentUser} rsvpOpen={rsvpOpen} onUpdated={onUpdated} />
          ) : (
            !started && !join.disabled && (
              <a href={join.href} target="_blank" rel="noreferrer noopener" className="block">
                <Button className="w-full bg-purple-700 text-white" data-testid="live-event-external">{join.label}</Button>
              </a>
            )
          )}
          {ev.is_rgm_studio && !started && status !== "ended" && (
            <p className="text-xs text-gray-500" data-testid="live-event-rsvp-note">
              Reserving is free and optional — anyone can join once the host goes live.
            </p>
          )}
        </div>
      </Card>

      {canManage && ev.is_rgm_studio && (
        <Card className="mt-6 p-6 bg-black/50 border-purple-500/20" data-testid="live-event-host-panel">
          <h2 className="text-lg font-bold text-white mb-4">Registered attendees</h2>
          <EventAttendeesList eventId={ev.event_id} refreshKey={attendeesKey} />
        </Card>
      )}
    </Shell>
  );
}

const StatusChip = ({ status, started }) => {
  const label = started ? "LIVE NOW" : status === "ended" ? "ENDED" : "UPCOMING";
  const cls = started ? "border-red-400/60 text-red-100" : status === "ended" ? "border-gray-400/40 text-gray-200" : "border-blue-400/50 text-blue-100";
  return (
    <span className={`absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-black bg-black/60 border backdrop-blur ${cls}`}
      data-testid="live-event-status">{label}</span>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#08080c]">
    <nav className="fixed left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-purple-900/30" style={{ top: "var(--rgm-app-banner-h, 0px)" }}>
      <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" data-testid="live-event-nav-logo"><img src="/rgm-logo.png" alt="RGM Studio" className="h-10 w-auto" /></Link>
        <Link to="/live" className="text-sm text-gray-300 hover:text-white flex items-center gap-1" data-testid="live-event-back">
          <ArrowLeft className="w-4 h-4" /> All live events
        </Link>
      </div>
    </nav>
    <main className="max-w-3xl mx-auto px-4 pt-24 pb-16">{children}</main>
  </div>
);
